/**
 * BoardCanvas - React Flow canvas component with infinite canvas behavior
 *
 * Extracted from BoardPage to separate canvas concerns from page layout.
 *
 * Key configurations:
 * - deleteKeyCode={[]} - Prevents Backspace from deleting nodes while typing
 * - Zoom limits (0.1x to 4x) for usable zoom range
 * - Selection mode with drag-to-select
 * - Pan with left and middle mouse buttons
 *
 * CRITICAL: nodeTypes is defined OUTSIDE the component to prevent re-renders.
 * Moving it inside would cause React Flow to recreate nodes on every render.
 */

import { useEffect, useCallback, useRef, useState, type MouseEvent, type RefObject } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useReactFlow,
  addEdge,
  applyNodeChanges,
  SelectionMode,
  ConnectionMode,
  type Node,
  type Edge,
  type NodeChange,
  type EdgeChange,
  type Connection,
} from '@xyflow/react';
import * as Y from 'yjs';
import { useNodeSelection, useDeleteShortcut } from '../../hooks/useNodeSelection';
// Import nodeTypes from centralized registry (MUST be module-level for performance)
import { nodeTypes } from '../nodes';
// Import edgeTypes and EdgeProvider for custom edge rendering
import { edgeTypes, EdgeProvider } from '../edges';
// Import edge data factory for smart connection detection
import { createAppEdgeData } from '@deep-agents/shared-types';
// Import NodeToolbar for creating nodes
import { NodeToolbar } from './NodeToolbar';
// Import theme components
import { useTheme, ThemeSwitcher, MacOSLayout, MacOSDock } from '../themes';
// Import AI presence components for Ghost Writer
import { AIPresence } from './AIPresence';

// ============================================================================
// REMOTE CURSOR COMPONENT
// ============================================================================

interface CursorData {
  id: string;
  name: string;
  color: string;
  x: number;
  y: number;
}

/**
 * RemoteCursors renders cursor positions for other users.
 * Uses flow-to-screen coordinate conversion for accurate positioning.
 */
function RemoteCursors({
  cursors,
  containerRef,
}: {
  cursors: CursorData[];
  containerRef: RefObject<HTMLDivElement | null>;
}) {
  const { flowToScreenPosition } = useReactFlow();
  const containerRect = containerRef.current?.getBoundingClientRect();

  return (
    <>
      {cursors.map((cursor) => {
        // Convert flow coordinates to screen coordinates for rendering
        const screenPos = flowToScreenPosition({ x: cursor.x, y: cursor.y });
        const left = containerRect ? screenPos.x - containerRect.left : screenPos.x;
        const top = containerRect ? screenPos.y - containerRect.top : screenPos.y;

        return (
          <div
            key={cursor.id}
            style={{
              position: 'absolute',
              left,
              top,
              zIndex: 9999,
              pointerEvents: 'none',
            }}
          >
            {/* Cursor arrow SVG */}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ display: 'block' }}
            >
              <path
                d="M5 2L19 12L12 13L9 20L5 2Z"
                fill={cursor.color}
                stroke="white"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
            {/* Cursor name label */}
            <div
              style={{
                position: 'absolute',
                left: '16px',
                top: '20px',
                backgroundColor: cursor.color,
                color: 'white',
                fontSize: '12px',
                fontWeight: 500,
                padding: '2px 8px',
                borderRadius: '4px',
                whiteSpace: 'nowrap',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }}
            >
              {cursor.name}
            </div>
          </div>
        );
      })}
    </>
  );
}

// ============================================================================
// BOARD CANVAS COMPONENT
// ============================================================================

export interface BoardCanvasProps {
  /** Yjs document for real-time sync */
  ydoc: Y.Doc;
  /** Awareness for collaborative cursors and presence */
  awareness?: any;
  /** Board ID for context (used by BlockNode for comments) */
  boardId?: string;
  /** Current user info for avatar and cursor color */
  currentUser: { id: string; name: string; color: string };
  /** Awareness states from all connected users */
  states: Map<number, any>;
  /** Local Yjs client ID to filter out self from remote cursors */
  localClientId: number;
  /** Callback to update cursor position in awareness */
  setCursor: (cursor: { x: number; y: number } | null) => void;
  /**
   * CRITICAL: All Y.Doc changes MUST use this wrapper for undo to work.
   * Wraps changes in a Yjs transaction with the user's origin for tracking.
   */
  makeChange: (fn: () => void) => void;
  /**
   * Callback when node selection changes (for PropertyPanel integration).
   * Passes the array of selected nodes to parent component.
   */
  onNodeSelectionChange?: (nodes: Node[]) => void;
  /**
   * Callback to open template gallery modal.
   * Called when user clicks "Templates" button in NodeToolbar.
   */
  onOpenTemplates?: () => void;
  /**
   * WebSocket reference for AI Ghost Writer requests (Phase 7).
   * Used by NodeToolbar to send AI canvas creation requests.
   */
  wsRef?: RefObject<WebSocket | null>;
}

/**
 * BoardCanvas - The main React Flow canvas with Yjs synchronization.
 *
 * Features:
 * - Infinite canvas with pan (left/middle mouse) and zoom (wheel)
 * - Nodes and edges synced via Yjs maps
 * - Real-time cursor display for collaborators
 * - Selection box for multi-select (drag on empty space)
 *
 * IMPORTANT: Delete key is disabled at React Flow level.
 * Custom delete handling is done via useDeleteShortcut hook (Task 3).
 */
export function BoardCanvas({
  ydoc,
  awareness,
  boardId,
  currentUser,
  states,
  localClientId,
  setCursor,
  makeChange,
  onNodeSelectionChange,
  onOpenTemplates,
  wsRef,
}: BoardCanvasProps) {
  const { screenToFlowPosition } = useReactFlow();
  const canvasRef = useRef<HTMLDivElement>(null);

  // -------------------------------------------------------------------------
  // Yjs Maps for nodes and edges (synced across all users)
  // -------------------------------------------------------------------------
  const nodesMap = ydoc.getMap<Node>('nodes');
  const edgesMap = ydoc.getMap<Edge>('edges');

  useEffect(() => {
    const updates: Array<[string, Node]> = [];
    nodesMap.forEach((node, id) => {
      const data = node.data as Record<string, unknown> | undefined;
      if (!data) return;

      const hasRuntimeProps =
        'ydoc' in data ||
        'awareness' in data ||
        'boardId' in data ||
        'nodesMap' in data ||
        'edgesMap' in data ||
        'makeChange' in data ||
        'onTitleChange' in data ||
        'onIncomingConnectionsChange' in data;

      if (!hasRuntimeProps) return;

      const nextData = { ...data };
      delete nextData.ydoc;
      delete nextData.awareness;
      delete nextData.boardId;
      delete nextData.nodesMap;
      delete nextData.edgesMap;
      delete nextData.makeChange;
      delete nextData.onTitleChange;
      delete nextData.onIncomingConnectionsChange;

      updates.push([
        id,
        {
          ...node,
          data: nextData,
        },
      ]);
    });

    if (updates.length === 0) {
      return;
    }

    makeChange(() => {
      for (const [id, cleaned] of updates) {
        nodesMap.set(id, cleaned);
      }
    });
  }, [nodesMap, makeChange]);

  // -------------------------------------------------------------------------
  // Selection state management
  // Using custom hook instead of React Flow's built-in to handle delete safely
  // -------------------------------------------------------------------------
  const { selectedNodeIds, onSelectionChange: baseSelectionChange } = useNodeSelection();

  /**
   * Extended selection change handler that:
   * 1. Updates internal selection state (for delete handling)
   * 2. Notifies parent component (for PropertyPanel)
   */
  const handleSelectionChange = useCallback(
    ({ nodes }: { nodes: Node[] }) => {
      // Update internal selection state (for delete shortcut)
      baseSelectionChange({ nodes });

      // Notify parent component if callback provided (for PropertyPanel)
      if (onNodeSelectionChange) {
        onNodeSelectionChange(nodes);
      }
    },
    [baseSelectionChange, onNodeSelectionChange]
  );

  /**
   * Custom delete handler that respects text inputs.
   * Called by useDeleteShortcut when Delete/Backspace is pressed
   * and user is NOT typing in an input field.
   */
  const handleDeleteSelected = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;

      // Wrap deletion in makeChange for undo tracking
      makeChange(() => {
        for (const id of ids) {
          nodesMap.delete(id);
        }
      });
    },
    [nodesMap, makeChange]
  );

  // Register keyboard shortcut for delete (respects text inputs)
  useDeleteShortcut(handleDeleteSelected, selectedNodeIds);

  // React state for rendering - updated from Yjs observers
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);

  // -------------------------------------------------------------------------
  // Sync Yjs maps to React state
  // -------------------------------------------------------------------------
  useEffect(() => {
    const mergeNodes = (incoming: Node[], existing: Node[]) => {
      const existingById = new Map(existing.map((node) => [node.id, node]));
      return incoming.map((node) => {
        const prev = existingById.get(node.id);
        if (!prev) return node;
        return {
          ...node,
          measured: prev.measured,
          selected: prev.selected,
          dragging: prev.dragging,
          resizing: prev.resizing,
          width: prev.width ?? node.width,
          height: prev.height ?? node.height,
        };
      });
    };

    const syncNodes = () => {
      const nextNodes = Array.from(nodesMap.values()).map((node) => {
        // Inject collaboration props into BlockNode data
        // These are runtime-only props not stored in Yjs
        if (node.type === 'block') {
          return {
            ...node,
            data: {
              ...node.data,
              ydoc,
              awareness,
              boardId,
              nodesMap,
              makeChange,
              onTitleChange: (newTitle: string) => {
                makeChange(() => {
                  const existing = nodesMap.get(node.id);
                  if (existing) {
                    nodesMap.set(node.id, {
                      ...existing,
                      data: { ...existing.data, title: newTitle },
                    });
                  }
                });
              },
            },
          };
        }
        // Inject runtime props into GeneratorNode data
        // These are needed for AI generation and data flow
        if (node.type === 'generator') {
          return {
            ...node,
            data: {
              ...node.data,
              ydoc,
              boardId,
              nodesMap,
              // Inject change handlers that write directly to Yjs
              // These ensure prompt/title/model changes are persisted
              onTitleChange: (newTitle: string) => {
                makeChange(() => {
                  const existing = nodesMap.get(node.id);
                  if (existing) {
                    nodesMap.set(node.id, {
                      ...existing,
                      data: { ...existing.data, title: newTitle },
                    });
                  }
                });
              },
              onPromptChange: (newPrompt: string) => {
                makeChange(() => {
                  const existing = nodesMap.get(node.id);
                  if (existing) {
                    nodesMap.set(node.id, {
                      ...existing,
                      data: { ...existing.data, prompt: newPrompt },
                    });
                  }
                });
              },
              onModelChange: (newModel: string) => {
                makeChange(() => {
                  const existing = nodesMap.get(node.id);
                  if (existing) {
                    nodesMap.set(node.id, {
                      ...existing,
                      data: { ...existing.data, model: newModel },
                    });
                  }
                });
              },
            },
          };
        }
        // Inject runtime props into KnowledgeNode data
        // These are needed for Knowledge Space API calls and output node creation
        if (node.type === 'knowledge') {
          return {
            ...node,
            data: {
              ...node.data,
              ydoc,
              boardId,
              nodesMap,
              edgesMap,
              makeChange,
              onTitleChange: (newTitle: string) => {
                makeChange(() => {
                  const existing = nodesMap.get(node.id);
                  if (existing) {
                    nodesMap.set(node.id, {
                      ...existing,
                      data: { ...existing.data, title: newTitle },
                    });
                  }
                });
              },
            },
          };
        }
        // Inject runtime props into ChatNode data
        // These are needed for message storage, API calls, and output node creation
        if (node.type === 'chat') {
          return {
            ...node,
            data: {
              ...node.data,
              ydoc,
              boardId,
              nodesMap,
              edgesMap,
              makeChange,
              onTitleChange: (newTitle: string) => {
                makeChange(() => {
                  const existing = nodesMap.get(node.id);
                  if (existing) {
                    nodesMap.set(node.id, {
                      ...existing,
                      data: { ...existing.data, title: newTitle },
                    });
                  }
                });
              },
            },
          };
        }
        // Inject runtime props into DesignerNode data
        // These are needed for Yjs-based element updates and undo/redo
        if (node.type === 'designer') {
          return {
            ...node,
            data: {
              ...node.data,
              nodesMap,
              makeChange,
            },
          };
        }
        // Inject runtime props into AppScreenNode data
        // These are needed for Yjs-based widget updates and undo/redo
        if (node.type === 'appScreen') {
          return {
            ...node,
            data: {
              ...node.data,
              nodesMap,
              edgesMap,
              makeChange,
            },
          };
        }
        // Inject runtime props into CodeNode data
        // These are needed for execution API calls and Yjs persistence
        if (node.type === 'code') {
          return {
            ...node,
            data: {
              ...node.data,
              ydoc,
              boardId,
              nodesMap,
              makeChange,
              onTitleChange: (newTitle: string) => {
                makeChange(() => {
                  const existing = nodesMap.get(node.id);
                  if (existing) {
                    nodesMap.set(node.id, {
                      ...existing,
                      data: { ...existing.data, title: newTitle },
                    });
                  }
                });
              },
              onCodeChange: (newCode: string) => {
                makeChange(() => {
                  const existing = nodesMap.get(node.id);
                  if (existing) {
                    nodesMap.set(node.id, {
                      ...existing,
                      data: { ...existing.data, code: newCode },
                    });
                  }
                });
              },
              onLanguageChange: (newLanguage: string) => {
                makeChange(() => {
                  const existing = nodesMap.get(node.id);
                  if (existing) {
                    nodesMap.set(node.id, {
                      ...existing,
                      data: { ...existing.data, language: newLanguage },
                    });
                  }
                });
              },
            },
          };
        }
        return node;
      });
      setNodes((prev) => mergeNodes(nextNodes, prev));
    };

    const syncEdges = () => {
      setEdges(Array.from(edgesMap.values()));
    };

    // Initial load from Yjs
    syncNodes();
    syncEdges();

    // Subscribe to Yjs changes (from local or remote users)
    nodesMap.observe(syncNodes);
    edgesMap.observe(syncEdges);

    return () => {
      nodesMap.unobserve(syncNodes);
      edgesMap.unobserve(syncEdges);
    };
  }, [nodesMap, edgesMap, ydoc, awareness, boardId, makeChange]);

  // -------------------------------------------------------------------------
  // Handle node changes (drag, resize, select, etc.)
  // IMPORTANT: All Y.Map modifications wrapped in makeChange() for undo tracking
  // -------------------------------------------------------------------------
  const onNodesChange = useCallback(
    (changes: NodeChange<Node>[]) => {
      // Always apply changes to local state so React Flow can track dimensions/selection
      setNodes((prev) => applyNodeChanges(changes, prev));

      // Filter to only MEANINGFUL changes that should be tracked for undo:
      // - 'position' (user dragging) - YES, track
      // - 'remove' (user deleting) - YES, track
      // - 'dimensions' (automatic measurement) - NO, don't track
      // - 'select' (selection changes) - NO, don't track
      const meaningfulChanges = changes.filter(c =>
        c.type === 'position' || c.type === 'remove'
      );

      if (meaningfulChanges.length === 0) {
        return;
      }

      // Get current nodes from Y.Map
      const currentNodes = Array.from(nodesMap.values());
      // Apply React Flow's change logic
      const nextNodes = applyNodeChanges(meaningfulChanges, currentNodes);

      // Further filter to only changes that actually modify the Y.Map
      const actualChanges: NodeChange<Node>[] = [];
      for (const change of meaningfulChanges) {
        if (change.type === 'remove') {
          // Only track if node actually exists in Y.Map
          if (nodesMap.has(change.id)) {
            actualChanges.push(change);
          }
        } else if (change.type === 'position') {
          // Only track if position actually changed
          const updatedNode = nextNodes.find((n) => n.id === change.id);
          const existing = nodesMap.get(change.id);
          if (updatedNode && existing) {
            // Compare positions (ignore other properties like selected, measured, etc.)
            const posChanged =
              existing.position.x !== updatedNode.position.x ||
              existing.position.y !== updatedNode.position.y;
            if (posChanged) {
              actualChanges.push(change);
            }
          }
        }
      }

      // Skip if no actual changes to make
      if (actualChanges.length === 0) {
        return;
      }

      // Wrap all Y.Map updates in makeChange() so UndoManager tracks them
      makeChange(() => {
        for (const change of actualChanges) {
          if (change.type === 'remove') {
            nodesMap.delete(change.id);
          } else if (change.type === 'position') {
            const updatedNode = nextNodes.find((n) => n.id === change.id);
            if (updatedNode) {
              // Only update position, preserve other properties from Y.Map
              const existing = nodesMap.get(change.id);
              if (existing) {
                nodesMap.set(change.id, {
                  ...existing,
                  position: updatedNode.position,
                });
              }
            }
          }
        }
      });
    },
    [nodesMap, makeChange]
  );

  // -------------------------------------------------------------------------
  // Handle edge changes (select, remove, etc.)
  // IMPORTANT: All Y.Map modifications wrapped in makeChange() for undo tracking
  // -------------------------------------------------------------------------
  const onEdgesChange = useCallback(
    (changes: EdgeChange<Edge>[]) => {
      // Only track 'remove' changes - 'add' is handled by onConnect
      // 'select' changes don't need undo tracking
      const meaningfulChanges = changes.filter(c => c.type === 'remove');

      if (meaningfulChanges.length === 0) {
        return;
      }

      // Filter to edges that actually exist in Y.Map
      const actualChanges = meaningfulChanges.filter(c =>
        c.type === 'remove' && edgesMap.has(c.id)
      );

      if (actualChanges.length === 0) {
        return;
      }

      // Wrap all Y.Map updates in makeChange() so UndoManager tracks them
      makeChange(() => {
        for (const change of actualChanges) {
          if (change.type === 'remove') {
            edgesMap.delete(change.id);
          }
        }
      });
    },
    [edgesMap, makeChange]
  );

  // -------------------------------------------------------------------------
  // Connection validation - Allow all connections between nodes
  // -------------------------------------------------------------------------
  const isValidConnection = useCallback(() => {
    // Allow all connections between nodes
    return true;
  }, []);

  // -------------------------------------------------------------------------
  // Handle new connections (edges)
  // IMPORTANT: Wrapped in makeChange() for undo tracking
  //
  // Smart edge detection for AppScreen nodes:
  // - AppScreen → AppScreen = Navigation edge (violet)
  // - AppScreen → Agent node = Agent trigger edge (amber)
  // - Knowledge → AppScreen = Data binding edge (green)
  // -------------------------------------------------------------------------
  const onConnect = useCallback(
    (params: Connection) => {
      // Get source and target node types to determine edge type
      const sourceNode = params.source ? nodesMap.get(params.source) : null;
      const targetNode = params.target ? nodesMap.get(params.target) : null;
      const sourceType = sourceNode?.type || 'default';
      const targetType = targetNode?.type || 'default';

      // Check if this involves AppScreen nodes
      const involvesAppScreen = sourceType === 'appScreen' || targetType === 'appScreen';

      // Create edge with appropriate type and data
      const currentEdges = Array.from(edgesMap.values());

      // Build edge with custom type if AppScreen involved
      let edgeToAdd: Edge;
      if (involvesAppScreen) {
        // Create app edge with smart defaults based on node types
        const edgeData = createAppEdgeData(sourceType, targetType);
        edgeToAdd = {
          id: `${params.source}-${params.target}`,
          source: params.source!,
          target: params.target!,
          sourceHandle: params.sourceHandle || undefined,
          targetHandle: params.targetHandle || undefined,
          // Use custom 'app' edge type for special rendering
          type: 'app',
          // Attach edge data for voice triggers, bindings, etc.
          // Cast needed because Edge type expects Record<string, unknown>
          data: edgeData as unknown as Record<string, unknown>,
        };
      } else {
        // Standard edge for non-app connections
        const nextEdges = addEdge(params, currentEdges);
        edgeToAdd = nextEdges.find((e) => !edgesMap.has(e.id)) || nextEdges[0];
      }

      // Wrap in makeChange() so new edges can be undone
      makeChange(() => {
        if (edgeToAdd && !edgesMap.has(edgeToAdd.id)) {
          edgesMap.set(edgeToAdd.id, edgeToAdd);
        }
      });
    },
    [edgesMap, nodesMap, makeChange]
  );

  // -------------------------------------------------------------------------
  // Edge update handler - for EdgeProvider context
  // Called when edge property popover saves changes
  // -------------------------------------------------------------------------
  const handleUpdateEdge = useCallback(
    (edgeId: string, newData: Record<string, unknown>) => {
      const existingEdge = edgesMap.get(edgeId);
      if (!existingEdge) return;

      makeChange(() => {
        edgesMap.set(edgeId, {
          ...existingEdge,
          data: newData,
        });
      });
    },
    [edgesMap, makeChange]
  );

  // -------------------------------------------------------------------------
  // Edge delete handler - for EdgeProvider context
  // -------------------------------------------------------------------------
  const handleDeleteEdge = useCallback(
    (edgeId: string) => {
      makeChange(() => {
        edgesMap.delete(edgeId);
      });
    },
    [edgesMap, makeChange]
  );

  // -------------------------------------------------------------------------
  // Cursor tracking - coalesce updates to animation frames for performance
  // -------------------------------------------------------------------------
  const cursorFrameRef = useRef<number | null>(null);
  const pendingCursorRef = useRef<{ x: number; y: number } | null>(null);
  const lastCursorRef = useRef<{ x: number; y: number } | null>(null);

  const flushCursor = useCallback(() => {
    cursorFrameRef.current = null;
    const next = pendingCursorRef.current;
    if (!next) return;
    pendingCursorRef.current = null;

    const last = lastCursorRef.current;
    // Only update if cursor moved at least 1px (prevents micro-updates)
    if (!last || Math.abs(next.x - last.x) >= 1 || Math.abs(next.y - last.y) >= 1) {
      lastCursorRef.current = next;
      setCursor(next);
    }
  }, [setCursor]);

  const scheduleCursorUpdate = useCallback((next: { x: number; y: number }) => {
    pendingCursorRef.current = next;
    if (cursorFrameRef.current === null) {
      cursorFrameRef.current = requestAnimationFrame(flushCursor);
    }
  }, [flushCursor]);

  useEffect(() => {
    return () => {
      if (cursorFrameRef.current !== null) {
        cancelAnimationFrame(cursorFrameRef.current);
      }
      setCursor(null);
    };
  }, [setCursor]);

  // Mouse move handler - convert screen to flow coordinates
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const flowPosition = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      scheduleCursorUpdate(flowPosition);
    },
    [screenToFlowPosition, scheduleCursorUpdate]
  );

  // Also update cursor on node drag
  const handleNodeDrag = useCallback(
    (e: MouseEvent) => {
      const flowPosition = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      scheduleCursorUpdate(flowPosition);
    },
    [screenToFlowPosition, scheduleCursorUpdate]
  );

  const handleMouseLeave = useCallback(() => {
    pendingCursorRef.current = null;
    lastCursorRef.current = null;
    if (cursorFrameRef.current !== null) {
      cancelAnimationFrame(cursorFrameRef.current);
      cursorFrameRef.current = null;
    }
    setCursor(null);
  }, [setCursor]);

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      const flowPosition = screenToFlowPosition({ x: e.clientX, y: e.clientY });
      scheduleCursorUpdate(flowPosition);
    },
    [screenToFlowPosition, scheduleCursorUpdate]
  );

  useEffect(() => {
    const container = canvasRef.current;
    if (!container) return;

    container.addEventListener('pointermove', handlePointerMove, { passive: true });
    container.addEventListener('pointerleave', handleMouseLeave);

    return () => {
      container.removeEventListener('pointermove', handlePointerMove);
      container.removeEventListener('pointerleave', handleMouseLeave);
    };
  }, [handlePointerMove, handleMouseLeave]);

  // -------------------------------------------------------------------------
  // Build remote cursors from awareness states
  // -------------------------------------------------------------------------
  const remoteCursors: CursorData[] = [];
  states.forEach((state, clientId) => {
    if (clientId !== localClientId && state.canvasCursor && state.user) {
      remoteCursors.push({
        id: String(clientId),
        name: state.user.name,
        color: state.user.color,
        x: state.canvasCursor.x,
        y: state.canvasCursor.y,
      });
    }
  });

  // -------------------------------------------------------------------------
  // Get theme
  // -------------------------------------------------------------------------
  const { theme } = useTheme();
  const isMacOS = theme === 'macos-classic';

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // Core canvas content (shared between themes)
  // Wrapped with EdgeProvider to allow edges to update their own data
  const canvasContent = (
    <EdgeProvider onUpdateEdge={handleUpdateEdge} onDeleteEdge={handleDeleteEdge} nodes={nodes}>
      <div
        ref={canvasRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          backgroundColor: isMacOS ? 'transparent' : '#ffffff',
        }}
      >
        <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        isValidConnection={isValidConnection}
        connectionMode={ConnectionMode.Loose}
        onPaneMouseMove={handleMouseMove}
        onPaneMouseLeave={handleMouseLeave}
        onNodeDrag={handleNodeDrag}
        onSelectionChange={handleSelectionChange}
        // ---------------------------------------------------------------
        // CRITICAL: Disable default delete keys to prevent conflicts
        // with text input editing. Custom delete handling is in Task 3.
        // ---------------------------------------------------------------
        deleteKeyCode={[]}
        // ---------------------------------------------------------------
        // Infinite canvas configuration
        // ---------------------------------------------------------------
        minZoom={0.1}
        maxZoom={4}
        fitView={false}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        // ---------------------------------------------------------------
        // Selection configuration
        // - SelectionMode.Partial: select nodes partially in selection box
        // - selectionOnDrag: enable drag-to-select on empty space
        // - panOnDrag: pan with left [0] and middle [1] mouse buttons
        //   (array format: 0=left, 1=middle, 2=right)
        // ---------------------------------------------------------------
        selectionMode={SelectionMode.Partial}
        selectionOnDrag={true}
        panOnDrag={[1, 2]}
        proOptions={{ hideAttribution: true }}
        style={{ width: '100%', height: '100%' }}
      >
        <Background
          color={isMacOS ? '#B0B0B0' : '#d1d5db'}
          gap={isMacOS ? 16 : 24}
          size={1}
        />
        <Controls />
        <MiniMap />
      </ReactFlow>

      {/* Node creation toolbar - only show in modern theme */}
      {!isMacOS && (
        <NodeToolbar
          nodesMap={nodesMap}
          makeChange={makeChange}
          onOpenTemplates={onOpenTemplates || (() => {})}
          ydoc={ydoc}
          awareness={awareness}
          boardId={boardId}
          wsRef={wsRef}
        />
      )}

      {/* Mac OS Dock - only show in macOS theme */}
      {isMacOS && (
        <MacOSDock nodesMap={nodesMap} makeChange={makeChange} />
      )}

      {/* Theme switcher - always visible */}
      <ThemeSwitcher />

      {/* Remote cursors overlay */}
      <RemoteCursors cursors={remoteCursors} containerRef={canvasRef} />

      {/* AI presence overlay (cursor + working indicator) */}
      <AIPresence states={states} containerRef={canvasRef} />
      </div>
    </EdgeProvider>
  );

  // Wrap in MacOS layout if theme is active
  if (isMacOS) {
    return <MacOSLayout>{canvasContent}</MacOSLayout>;
  }

  return canvasContent;
}
